import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { signaturesAPI } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, PenTool, RotateCcw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SignatureDialog({ open, onOpenChange, documentType, documentId, onSigned }) {
  const sigRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const handleClear = () => {
    if (sigRef.current) sigRef.current.clear();
  };

  const handleSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please draw your signature first');
      return;
    }
    setSaving(true);
    try {
      const signatureData = sigRef.current.toDataURL('image/png');
      await signaturesAPI.sign(documentType, documentId, { signature: signatureData });
      toast.success('Document signed successfully');
      if (onSigned) onSigned(signatureData);
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save signature');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenTool className="h-5 w-5" />Sign Document</DialogTitle>
          <DialogDescription>Draw your signature below to approve this {documentType?.replace('_', ' ')}</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="border-2 border-dashed border-border rounded-lg bg-white" data-testid="signature-canvas-wrapper">
            <SignatureCanvas
              ref={sigRef}
              canvasProps={{
                width: 400,
                height: 160,
                className: 'w-full rounded-lg',
                style: { maxWidth: '100%' },
              }}
              penColor="#1e1e1e"
              dotSize={1.5}
              minWidth={1}
              maxWidth={2.5}
            />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">Draw your signature using mouse or touch</p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClear} data-testid="clear-signature-btn">
            <RotateCcw className="h-4 w-4 mr-2" />Clear
          </Button>
          <Button onClick={handleSign} disabled={saving} data-testid="confirm-signature-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Sign & Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
